import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { activeNetworkSelector, userSelector } from 'redux/selectors';
import {
  deriveTier,
  isCustomer as isCustomerFn,
  hasCapability as hasCapabilityFn,
  hasAccessToContacts as hasAccessToContactsFn,
  hasContactsLimit as hasContactsLimitFn,
  isContactsLimitReached as isContactsLimitReachedFn,
  resolveFindemUserId,
} from 'services/tierGating';

/**
 * @param {object} [options] - Optional overrides
 * @param {object} [options.network] - Network to use for tier/capability checks. When provided (e.g. onboarding
 *   context network from route/company), tier gating is evaluated against this network instead of Redux active.
 *   Use this when the UI context network can differ from the globally active network (e.g. company onboarding).
 */
export const useTierGating = (options = {}) => {
  const reduxActiveNetwork = useSelector(activeNetworkSelector);
  const user = useSelector(userSelector);
  const network = options.network ?? reduxActiveNetwork;

  return useMemo(() => {
    const tierResult = deriveTier(network);
    const customer = isCustomerFn(network);
    // Findem identity is per network: the network's override (userFindemUserId)
    // wins over the user's global findemUserId.
    const gatingUser = user ? { ...user, findemUserId: resolveFindemUserId(network, user) } : user;

    return {
      tierResult,
      isCustomer: customer,
      hasCapability: (capability) => hasCapabilityFn(tierResult, capability, gatingUser),
      hasAccessToContacts: hasAccessToContactsFn(tierResult),
      hasContactsLimit: hasContactsLimitFn(tierResult, network),
      isContactsLimitReached: isContactsLimitReachedFn(tierResult, network),
    };
  }, [network, user]);
};
